import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Select, MenuItem, InputLabel, FormControl,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SimpleDatePicker from '../../../../ui/simpleDatePicker';
import { formatAmountForDisplayWithPreferences, formatDateForServer, formatDateWithTimezone, parseMoney, sumMoney } from 'utils';
import { ValidDataService } from 'service/validDataService';
import { toast } from 'react-toastify';
import CreditMemoService from 'service/creditMemoService';
import { usePreferences } from 'hooks/usePreferences';

// Helper component for the Add/Edit Payment Dialog
function AddEditPaymentDialog({ open, onClose, paymentData, onSave, invoiceBalance, customerId }) {
    const [paymentMethodId, setPaymentMethodId] = useState(''); // Store payment method ID
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentAmount, setAmount] = useState('');
    const [paymentDate, setDate] = useState(''); // Use Date object internally
    const [errors, setErrors] = useState({});
    const [loadingCredits, setLoadingCredits] = useState(false);
    const [creditMemos, setCreditMemos] = useState([]);
    const [amountsToApply, setAmountsToApply] = useState({}); // Tracks { creditMemoId: amount }
    const { preferences } = usePreferences();

    // Populate form when paymentData changes (for editing)
    useEffect(() => {

        (async () => {
            if (!open) return; // Only fetch if dialog is open
            setCreditMemos([]);
            // Fetch valid payment methods
            const paymentMethods = await ValidDataService.getValidPaymentMethods();
            const { code, data, message } = paymentMethods;
            if (code === 0) {
                setPaymentMethods(data);
            } else {
                toast.error(message);
                console.error('Error fetching payment methods:', paymentMethods.message);
            }

            if (paymentData) {
                setPaymentMethodId(paymentData.paymentMethodId || '');
            } else {
                setPaymentMethodId('');
            }

            setErrors({}); // Clear errors on open/data change
        }
        )()

        if (open && paymentData) {
            setAmount(paymentData.paymentAmount || '');
            setDate(formatDateWithTimezone(paymentData.paymentDate)); // Convert to Date object
        } else {
            setDate('');
            setAmount('');
        }

    }, [open, paymentData]); // Depend on open and paymentData

    const handleAmountChange = (event) => {
        const value = event.target.value;
        // Allow only numbers and a single decimal point
        if (/^\d*\.?\d*$/.test(value)) {
            setAmount(value);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!paymentMethodId) newErrors.paymentMethod = 'Payment paymentMethod is required';
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) newErrors.paymentAmount = 'Amount must be a positive number';
        if (!paymentDate) newErrors.paymentDate = 'Date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            console.error('Validation failed', errors);
            return;
        }
        const selectedMethod = paymentMethods.find(method => method.id === paymentMethodId);

        const savedPayment = {
            ...paymentData, // Include id if editing
            paymentMethodId: paymentMethodId,
            name: selectedMethod ? selectedMethod.name : '',
            paymentAmount: parseFloat(paymentAmount), // Store paymentAmount as a number
            paymentDate: paymentDate, // Store paymentDate as ISO string (or just the Date object)
            ...(isStoreCreditSelected && {
                creditMemos: Object.entries(amountsToApply)
                    .filter(([_, amount]) => parseFloat(amount) > 0)
                    .map(([creditMemoId, amount]) => ({ creditMemoId, amountToApply: parseFloat(amount) })),
                isStoreCredit: true
            })
        };
        onSave(savedPayment);
    };

    const handlePaymentMethodChange = async (event) => {
        const methodId = event.target.value;
        setPaymentMethodId(methodId);

        // Check if Store Credit is selected
        const selectedMethod = paymentMethods.find(method => method.id === methodId);
        if (selectedMethod && selectedMethod.name === 'Store Credit') {
            setLoadingCredits(true);
            try {
                const response = await CreditMemoService.getAvailableCreditMemosByCustomerId(customerId);
                if (response.code === 0) {
                    console.log('Available store credits:', response.data);
                    const approvedCredits = response.data.filter(credit => credit.status === 'approved');
                    setCreditMemos(approvedCredits);
                }
            } catch (error) {
                console.error('Error fetching store credits:', error);
                toast.error('Failed to load available store credits');
            }
            setLoadingCredits(false);
        } else {
            setCreditMemos([]);
        }
    }

    const handleCreditAmountChange = (creditMemoId, value, maxAmount) => {
        const numericValue = parseFloat(value);
        if (value === '' || (numericValue >= 0 && numericValue <= maxAmount)) {
            setAmountsToApply(prev => ({
                ...prev,
                [creditMemoId]: value
            }));
        }
    };

    const totalApplied = useMemo(() => {
        return Object.values(amountsToApply).reduce((sum, amount) => {
            const val = parseFloat(amount);
            return sum + (isNaN(val) ? 0 : val);
        }, 0);
    }, [amountsToApply]);

        // is store credit selected
    const isStoreCreditSelected = paymentMethodId && paymentMethods.find(method => method.id === paymentMethodId)?.name === 'Store Credit';

    // Update paymentAmount when store credit amounts change
    useEffect(() => {
        if (isStoreCreditSelected) {
            setAmount(totalApplied.toString());
        }
    }, [totalApplied, isStoreCreditSelected]);

    const isApplyDisabled = totalApplied <= 0 || totalApplied > invoiceBalance;


    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{paymentData ? 'Edit Payment' : 'Add New Payment'}</DialogTitle>
            <DialogContent dividers>
                {
                    paymentMethods.length === 0 ? (
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="large"
                            label="Loading payment methods..."
                            InputProps={{
                                endAdornment: <CircularProgress color="inherit" size={20} />
                            }}
                        />)
                        :
                        <FormControl fullWidth margin="normal" error={!!errors.paymentMethod}>
                            <InputLabel id="payment-paymentMethod-label">Method</InputLabel>
                            <Select
                                labelId="payment-paymentMethod-label"
                                id="payment-paymentMethod-select"
                                value={paymentMethodId}
                                label="Method"
                                onChange={handlePaymentMethodChange}
                            >
                                <MenuItem value=""><em>Select Method</em></MenuItem>
                                {paymentMethods.map((method) => (
                                    <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                                ))}
                            </Select>
                            {errors.paymentMethod && <Typography color="error" variant="caption">{errors.paymentMethod}</Typography>}
                        </FormControl>
                }
                {loadingCredits && <Typography variant="caption">Loading available credits... </Typography>}
                {!loadingCredits &&isStoreCreditSelected && creditMemos.length === 0 ? (
                    <Typography>No available store credit found for this customer.</Typography>
                ) : (
                    <>
                        <List>
                            {creditMemos.map(memo => (
                                <ListItem key={memo.id} divider>
                                    <ListItemText
                                        primary={`Credit Memo #${memo.creditMemoNumber}`}
                                        secondary={`Available: ${formatAmountForDisplayWithPreferences(memo.remainingAmount, preferences)}`}
                                    />
                                    <TextField
                                        sx={{ width: '150px', ml: 2 }}
                                        label="Amount to Apply"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={amountsToApply[memo.id] || ''}
                                        onChange={(e) => handleCreditAmountChange(memo.id, e.target.value, memo.remainingAmount)}
                                        InputProps={{
                                            startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                            {
                                isStoreCreditSelected &&
                                <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1, textAlign: 'right' }}>
                                    <Typography variant="body1">Invoice Balance: {formatAmountForDisplayWithPreferences(invoiceBalance, preferences)}</Typography>
                                    <Typography variant="h6">Total Applying: {formatAmountForDisplayWithPreferences(totalApplied, preferences)}</Typography>
                                    {totalApplied > invoiceBalance && (
                                        <Typography color="error" variant="caption">
                                            Amount applied cannot exceed the invoice balance.
                                        </Typography>
                                    )}
                                </Box>
                            }
                    </>
                )}

                <TextField
                    label="Amount"
                    fullWidth
                    margin="normal"
                    value={paymentAmount}
                    onChange={handleAmountChange}
                    error={!!errors.paymentAmount}
                    helperText={errors.paymentAmount}
                    InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                        type: 'text', // Use text to control input format manually
                        inputMode: 'decimal'
                    }}
                />

                {/* Assuming DatePickerComponent handles paymentDate selection and value is a Date object */}
                <SimpleDatePicker
                    label="Date"
                    size='large'
                    value={paymentDate}
                    onChange={(value) => setDate(value)}
                    error={!!errors.paymentDate}
                    helperText={errors.paymentDate}
                />


            </DialogContent>
            <DialogActions>
                <Button onClick={handleSave} variant="contained">Save</Button>
                <Button color='cancel' variant='outlined' onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

// Main Payment Collector Component
export default function PaymentCollector({
    payments = [], // Array of payment objects { id, paymentMethod, paymentAmount, paymentDate }
    onAddPayment,
    onEditPayment,
    onDeletePayment,
    customerId,
    invoiceBalance,
    invoiceId,
    onRefresh
}) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null); // null for add, payment object for edit
    const [creditDialogOpen, setCreditDialogOpen] = useState(false);
    const { preferences } = usePreferences();

    const handleOpenAddDialog = () => {
        setEditingPayment(null); // Ensure we are adding
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (payment) => {
        setEditingPayment(payment); // Set payment data for editing
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSavePayment = (payment) => {
        if (payment.id || payment.tempId) {
            // Editing existing payment
            if (onEditPayment) {
                onEditPayment(payment);
            }
        } else {
            // Adding new payment
            const newPayment = { ...payment, tempId: Date.now() }; // Assign a simple unique ID
            if (onAddPayment) {
                onAddPayment(newPayment);
            }
        }
        handleCloseDialog(); // Close dialog after saving
    };

    const handleDeletePayment = (paymentId) => {
        
        if (onDeletePayment) {
            onDeletePayment(paymentId);
        }
    };

    const handleApplyCredit = async (creditsToApply) => {
        // Convert credits to payment objects for display
        const creditPayments = creditsToApply.map(credit => ({
            tempId: Date.now(),
            name: 'Store Credit',
            paymentAmount: credit.amountToApply,
            paymentDate: formatDateForServer(new Date()),
            creditMemoId: credit.creditMemoId,
            isStoreCredit: true
        }));
        // Add to payments array for display
        creditPayments.forEach(payment => onAddPayment(payment));
        setCreditDialogOpen(false);

        // try {
        //     for (const credit of creditsToApply) {
        //         await CreditMemoService.applyCreditMemo(
        //             credit.creditMemoId,
        //             invoiceId, // Pass the invoiceId to the service
        //             credit.amountToApply
        //         );
        //     }
        //     toast.success('Store credit applied successfully!');
        //     setCreditDialogOpen(false);
        //     if (onRefresh) {
        //         onRefresh(); // Refresh the parent component to show the new payment
        //     }
        // } catch (error) {
        //     console.error('Failed to apply store credit:', error);
        //     toast.error(error.message || 'Failed to apply store credit.');
        // }
    };

    // Calculate total paid paymentAmount
    const totalPaid = useMemo(() => sumMoney(payments.map(payment => parseMoney(payment.paymentAmount) || 0)), [payments]);

    return (
        <Box sx={{ mt: 1, border: '1px solid #ccc', p: 1, borderRadius: 1, maxHeight: '300px', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Payment Information</Typography>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                sx={{ mb: 2 }}
            >
                Add Payment
            </Button>
            {/* <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreditDialogOpen(true)}
                sx={{ mb: 2, ml: 2 }}
            >
                Apply Store Credit
            </Button> */}
            <TableContainer >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Method</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No payments recorded yet.</TableCell>
                            </TableRow>
                        ) : (
                            payments.map((payment) => (
                                <TableRow 
                                    key={payment.id || payment.tempId}
                                    sx={{
                                        opacity: payment._markedForDeletion ? 0.4 : 1,
                                        textDecoration: payment._markedForDeletion ? 'line-through' : 'none'
                                    }}
                                >
                                    <TableCell>
                                        {payment._markedForDeletion && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                    px: 0.5,
                                                    py: 0.25,
                                                    backgroundColor: '#d32f2f',
                                                    borderRadius: 0.5,
                                                    opacity: 1
                                                }}
                                            >
                                                MARKED FOR DELETION
                                            </Typography>
                                        )}
                                        {payment.name || payment.paymentMethod.name}
                                    </TableCell>
                                    <TableCell align="right">{formatAmountForDisplayWithPreferences(payment.paymentAmount || 0, preferences)}</TableCell>
                                    <TableCell>{payment.paymentDate ? formatDateWithTimezone(payment.paymentDate) : 'N/A'}</TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenEditDialog(payment)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeletePayment(payment.id || payment.tempId)}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">Total Paid: {formatAmountForDisplayWithPreferences(totalPaid, preferences)}</Typography>
            </Box>

            {/* Add/Edit Payment Dialog */}
            <AddEditPaymentDialog
                open={openDialog}
                onClose={handleCloseDialog}
                paymentData={editingPayment}
                onSave={handleSavePayment}
                invoiceBalance={invoiceBalance}
                customerId={customerId}
            />
            {/* <ApplyCreditDialog
                open={creditDialogOpen}
                onClose={() => setCreditDialogOpen(false)}
                onApply={handleApplyCredit}
                customerId={customerId}
                invoiceBalance={invoiceBalance}
            /> */}
        </Box>
    );
}